import { FC, useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { MenuItem, Grid, TextField, InputAdornment } from '@mui/material';
import { IDropdownResponse } from '../../../models';
import { getOptionSets, getOptionPhases } from '../../../fetch';
import { FiltersButtons, FiltersLayout, SMBSelect } from '../../../components';
import { Close, Search } from '@mui/icons-material';

interface IOptionFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setPage: (val: number) => void;
  setHasAppliedFilters: (val: boolean) => void;
  hasAppliedFilters?: boolean;
  searchName: string;
  setSearchName: (val: any) => void;
  handleNameSearch: (clearSearch?: boolean) => void;
  selectedPhaseId: number | string;
  setSelectedPhaseId: (val: string | null) => void;
  selectedOptionId: number | string;
  setSelectedOptionId: (val: string | null) => void;
}

export const OptionFilters: FC<IOptionFilters> = ({
  hasAppliedFilters,
  setHasAppliedFilters,
  isLoading,
  applyFilters,
  searchName,
  setSearchName,
  handleNameSearch,
  selectedPhaseId,
  setSelectedPhaseId,
  selectedOptionId,
  setSelectedOptionId
}) => {
  const [isLoadingOptionSets, setIsLoadingOptionSets] = useState(false);
  const [optionSets, setOptionSets] = useState<IDropdownResponse[]>([]);
  const [isLoadingPhases, setIsLoadingPhases] = useState(false);
  const [phases, setPhases] = useState<IDropdownResponse[]>([]);
  const classes = useStyles();

  const fetchOptionSets = async () => {
    setIsLoadingOptionSets(true);
    try {
      const res = await getOptionSets();
      setOptionSets(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingOptionSets(false);
    }
  };

  const fetchPhases = async () => {
    setIsLoadingPhases(true);
    try {
      const res = await getOptionPhases();
      setPhases(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingPhases(false);
    }
  };

  useEffect(() => {
    fetchOptionSets();
    fetchPhases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FiltersLayout>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Search Name..."
          name="nameSearch"
          value={searchName}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                className={classes.searchIcon}
                onClick={() => {
                  if (searchName.length > 0) {
                    setHasAppliedFilters(true);
                    handleNameSearch();
                  }
                }}
              >
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchName ? (
              <InputAdornment
                position="end"
                className={classes.searchIcon}
                onClick={() => {
                  setSearchName('');
                  handleNameSearch(true);
                }}
              >
                <Close />
              </InputAdornment>
            ) : null
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && searchName.length > 0) {
              setHasAppliedFilters(true);
              handleNameSearch();
            }
          }}
          onChange={e => {
            setSearchName(e.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <SMBSelect
          label={isLoadingOptionSets ? 'Loading...' : 'Option Set'}
          disabled={isLoading || isLoadingOptionSets}
          value={selectedOptionId ? selectedOptionId : ''}
          onChange={e => {
            setSelectedOptionId(e.target.value);
          }}
          handleClear={() => setSelectedOptionId('')}
        >
          {optionSets &&
            !isLoadingOptionSets &&
            optionSets.map((option, index) => {
              return (
                <MenuItem key={`${option.value}-${index}`} value={option.value}>
                  {option.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <SMBSelect
          label={isLoadingPhases ? 'Loading...' : 'Phase'}
          disabled={isLoading || isLoadingPhases}
          value={selectedPhaseId ? selectedPhaseId : ''}
          onChange={e => {
            setSelectedPhaseId(e.target.value);
          }}
          handleClear={() => setSelectedPhaseId('')}
        >
          {phases &&
            !isLoadingPhases &&
            phases.map((phase, index) => {
              return (
                <MenuItem key={`${phase.value}-${index}`} value={phase.value}>
                  {phase.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      <Grid item xs={12} lg={3}>
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSearchName(null);
            setSelectedOptionId(null);
            setSelectedPhaseId(null);
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  }
}));
